// Modules
import { ClipboardModule } from '@angular/cdk/clipboard';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { NgApexchartsModule } from 'ng-apexcharts';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { CarouselModule } from 'ngx-owl-carousel-o';
import { SwiperModule } from 'swiper/angular';
import { HttpLoaderFactory } from '../app.module';

// Services
import { LayoutService } from 'src/app/core/services/ui/layout.service';
import { NavService } from 'src/app/core/services/ui/nav.service';

// Components
import { TooglePanelComponent } from './components/accordions/toogle-panel/toogle-panel.component';
import { AlertPageConfigurationComponent } from './components/alerts/alert-page-configuration/alert-page-configuration.component';
import { BlockPlayerComponent } from './components/block-player/block-player.component';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { LightDescriptionComponent } from './components/cards/light-description/light-description.component';
import { ChangePasswordComponent } from './components/change-password/change-password.component';
import { ComingSoonComponent } from './components/coming-soon/coming-soon.component';
import { CustomizerComponent } from './components/customizer/customizer.component';
import { CustomDateSelectorComponent } from './components/datepicker/custom-date-selector/custom-date-selector.component';
import { DropdownGenericComponent } from './components/dropdowns/dropdown-generic/dropdown-generic.component';
import { DropdownReportExportComponent } from './components/dropdowns/dropdown-report-export/dropdown-report-export.component';
import { FeatherIconsComponent } from './components/feather-icons/feather-icons.component';
import { FooterComponent } from './components/footer/footer.component';
import { FormFieldErrorComponent } from './components/form-field-error/form-field-error.component';
import { AuthCheckFormComponent } from './components/forms/auth-check-form/auth-check-form.component';
import { AddUsersComponent } from './components/header/elements/add-users/add-users.component';
import { BalanceUserCardsComponent } from './components/header/elements/balance-user-cards/balance-user-cards.component';
import { BookmarkComponent } from './components/header/elements/bookmark/bookmark.component';
import { CartComponent } from './components/header/elements/cart/cart.component';
import { BalanceCardsFormComponent } from './components/header/elements/forms/balance-cards-form/balance-cards-form.component';
import { TransferUserFormComponent } from './components/header/elements/forms/transfer-user-form/transfer-user-form.component';
import { LanguagesComponent } from './components/header/elements/languages/languages.component';
import { MegaMenuComponent } from './components/header/elements/mega-menu/mega-menu.component';
import { MessageBoxComponent } from './components/header/elements/message-box/message-box.component';
import { MyAccountComponent } from './components/header/elements/my-account/my-account.component';
import { NotificationComponent } from './components/header/elements/notification/notification.component';
import { SearchComponent } from './components/header/elements/search/search.component';
import { SwiperComponent } from './components/header/elements/swiper/swiper.component';
import { TransferSelectorComponent } from './components/header/elements/transfer-selector/transfer-selector.component';
import { TransferUserModalComponent } from './components/header/elements/transfer-user-modal/transfer-user-modal.component';
import { UserTypeSelectorComponent } from './components/header/elements/user-type-selector/user-type-selector.component';
import { HeaderComponent } from './components/header/header.component';
import { ContentComponent } from './components/layout/content/content.component';
import { FullComponent } from './components/layout/full/full.component';
import { LoaderComponent } from './components/loader/loader.component';
import { BootstrapModalComponent } from './components/modals/bootstrap-modal/bootstrap-modal.component';
import { PageConfigurationModalComponent } from './components/modals/page-configuration-modal/page-configuration-modal.component';
import { MovePlayerComponent } from './components/move-player/move-player.component';
import { PlayerNoteFormComponent } from './components/player-notes/player-notes.component';
import { SelectsUsersComponent } from './components/selects/selects-users/selects-users.component';
import { SetLimitsComponent } from './components/set-limits/set-limits.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { SkeletonsCardsNgxDatatableMobileComponent } from './components/skeletons/skeletons-cards-ngx-datatable-mobile/skeletons-cards-ngx-datatable-mobile.component';
import { SvgIconComponent } from './components/svg-icon/svg-icon.component';
import { TableColumnCustomizerComponent } from './components/table-column-customizer-component/table-column-customizer-component.component';
import { NgxDatatableMobileCardsComponent } from './components/tables/ngx-datatable-mobile-cards/ngx-datatable-mobile-cards.component';
import { NgxDatatableComponent } from './components/tables/ngx-datatable/ngx-datatable.component';
import { TapToTopComponent } from './components/tap-to-top/tap-to-top.component';

// Directives
import { CustomBadgeDirective } from './directives/custom-badge.directive';
import { DecimalInputDirective } from './directives/decimal-input.directive';
import { DisableKeyPressDirective } from './directives/disable-key-press.directive';
import { MaxCharactersDirective } from './directives/max-characters.directive';
import { OnlyAlphabetsDirective } from './directives/only-alphabets.directive';
import { OnlyNumbersWhitTwoDecimalDirective } from './directives/only-numbers-whit-two-decimal.directive';
import { OnlyNumbersDirective } from './directives/only-numbers.directive';
import { PhoneInputDirective } from './directives/phone-input.directive';
import { RequiredAsteriskDirective } from './directives/required-asterisk.directive';
import { RestrictNegativeDirective } from './directives/restrict-negative.directive';
import { ShowForUserTypesDirective } from './directives/show-for-user-types.directive';
import { ShowWithPermissionDirective } from './directives/show-with-permission.directive';
import { ValidationBorderDirective } from './directives/validation-border.directive';

// Pipes
import { StatusToActiveInactivePipe } from './pipes/active-descative.pipe';
import { BooleanToActiveInactivePipe } from './pipes/boolean-to-active-inactive.pipe';
import { BooleanToYesNoPipe } from './pipes/boolean-to-yes-no.pipe';
import { CashdeskCommisionTypePipe } from './pipes/cashdesk-commision-type.pipe';
import { CashdeskTypePipe } from './pipes/cashdesk-type.pipe';
import { GenderToLabelPipe } from './pipes/gender.pipe';
import { LowercasePipe } from './pipes/lowercase.pipe';
import { SanitizeHtmlPipe } from './pipes/sanitize-html.pipe';
import { SocialNetworkBadgePipe } from './pipes/social-network-badge.pipe';
import { StatusMessagePipe } from './pipes/status-message.pipe';
import { BuildClientEnvironmentPipe } from './pipes/build-client-environment.pipe';

@NgModule({
  declarations: [
    // Componentes
    BlockPlayerComponent,
    BookmarkComponent,
    BootstrapModalComponent,
    BreadcrumbComponent,
    CartComponent,
    ChangePasswordComponent,
    ContentComponent,
    CustomDateSelectorComponent,
    CustomizerComponent,
    DropdownGenericComponent,
    DropdownReportExportComponent,
    FeatherIconsComponent,
    FooterComponent,
    FullComponent,
    HeaderComponent,
    LanguagesComponent,
    LoaderComponent,
    MegaMenuComponent,
    MessageBoxComponent,
    MovePlayerComponent,
    MyAccountComponent,
    NgxDatatableComponent,
    NgxDatatableMobileCardsComponent,
    NotificationComponent,
    PlayerNoteFormComponent,
    SearchComponent,
    SetLimitsComponent,
    SidebarComponent,
    SvgIconComponent,
    SwiperComponent,
    TapToTopComponent,
    TooglePanelComponent,
    BalanceUserCardsComponent,
    TransferSelectorComponent,
    TransferUserModalComponent,
    BalanceCardsFormComponent,
    TransferUserFormComponent,
    AddUsersComponent,
    ComingSoonComponent,
    FormFieldErrorComponent,
    AlertPageConfigurationComponent,
    UserTypeSelectorComponent,
    LightDescriptionComponent,
    TableColumnCustomizerComponent,
    SelectsUsersComponent,
    SkeletonsCardsNgxDatatableMobileComponent,
    PageConfigurationModalComponent,
    AuthCheckFormComponent,

    // Directivas
    DecimalInputDirective,
    DisableKeyPressDirective,
    MaxCharactersDirective,
    OnlyAlphabetsDirective,
    OnlyNumbersDirective,
    PhoneInputDirective,
    ShowWithPermissionDirective,
    CustomBadgeDirective,
    OnlyNumbersWhitTwoDecimalDirective,
    ShowWithPermissionDirective,
    RequiredAsteriskDirective,
    RestrictNegativeDirective,
    ValidationBorderDirective,
    ShowForUserTypesDirective,

    // Pipes
    BooleanToYesNoPipe,
    CashdeskCommisionTypePipe,
    CashdeskTypePipe,
    GenderToLabelPipe,
    StatusToActiveInactivePipe,
    StatusMessagePipe,
    BooleanToActiveInactivePipe,
    SocialNetworkBadgePipe,
    SanitizeHtmlPipe,
    LowercasePipe,
    BuildClientEnvironmentPipe,
  ],
  imports: [
    CarouselModule,
    CommonModule,
    AngularEditorModule,
    FormsModule,
    NgbModule,
    NgSelectModule,
    NgxDatatableModule,
    ReactiveFormsModule,
    NgxDropzoneModule,
    RouterModule,
    SwiperModule,
    DragDropModule,
    NgApexchartsModule,
    ClipboardModule,
    TranslateModule.forChild({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [NavService, LayoutService],
  exports: [
    // Modules
    AngularEditorModule,
    FormsModule,
    NgbModule,
    NgSelectModule,
    ReactiveFormsModule,
    TranslateModule,
    NgApexchartsModule,
    DragDropModule,
    ClipboardModule,

    // Components
    BlockPlayerComponent,
    BootstrapModalComponent,
    BreadcrumbComponent,
    ChangePasswordComponent,
    CustomDateSelectorComponent,
    DropdownGenericComponent,
    DropdownReportExportComponent,
    FeatherIconsComponent,
    LoaderComponent,
    MovePlayerComponent,
    NgxDatatableComponent,
    NgxDatatableMobileCardsComponent,
    PlayerNoteFormComponent,
    SelectsUsersComponent,
    SetLimitsComponent,
    SvgIconComponent,
    SwiperModule,
    TapToTopComponent,
    TooglePanelComponent,
    ComingSoonComponent,
    FormFieldErrorComponent,
    AlertPageConfigurationComponent,
    LightDescriptionComponent,
    TableColumnCustomizerComponent,
    MessageBoxComponent,
    PageConfigurationModalComponent,
    AuthCheckFormComponent,

    // Directives
    DecimalInputDirective,
    DisableKeyPressDirective,
    MaxCharactersDirective,
    OnlyAlphabetsDirective,
    OnlyNumbersDirective,
    PhoneInputDirective,
    ShowWithPermissionDirective,
    CustomBadgeDirective,
    OnlyNumbersWhitTwoDecimalDirective,
    RequiredAsteriskDirective,
    RestrictNegativeDirective,
    ShowForUserTypesDirective,
    ValidationBorderDirective,

    // Pipes
    BooleanToYesNoPipe,
    CashdeskCommisionTypePipe,
    CashdeskTypePipe,
    GenderToLabelPipe,
    StatusToActiveInactivePipe,
    StatusMessagePipe,
    BooleanToActiveInactivePipe,
    SocialNetworkBadgePipe,
    SanitizeHtmlPipe,
    LowercasePipe,
    BuildClientEnvironmentPipe,
  ],
})
export class SharedModule {}
